// helpers.ts

import { Model, Item, ModelTemplate, ItemTemplate, Warband, ExplorationLocation, ModelStats } from "./types";
import { normalizeBackgroundKey } from "./utils/backgroundSlug";

// Safe JSON parse with fallback to default value
const safeParse = <T>(key: string, defaultValue: T): T => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (e) {
    console.warn(`Invalid JSON in localStorage for ${key}:`, e);
    return defaultValue;
  }
};

// Constants
export const MODEL_KEYWORDS = [
  "NEGATE FEAR",
  "FEAR",
  "NEGATE GAS",
  "NEGATE FIRE",
  "NEGATE SHRAPNEL",
  "INFILTRATOR",
  "STRONG",
  "LEADER",
  "GOLEM",
  "ARTIFICIAL",
  "DEMONIC",
  "FLYING",
  "TOUGH",
  "NEW ANTIOCH",
  "TRENCH PILGRIMS",
  "IRON SULTANATE",
  "HERETIC",
  "BLACK GRAIL",
  "THE COURT",
  "ELITE",
  "SKIRMISHER",
];

export const WEAPON_KEYWORDS = [
  "+1 INJURY DICE",
  "+2 INJURY DICE",
  "-1 INJURY DICE",
  "IGNORE ARMOUR",
  "ARMOUR PIERCE",
  "AUTOMATIC (X)",
  "SHOTGUN",
  "FIRE",
  "SHRAPNEL",
  "GAS",
  "BLOCK",
  "CUMBERSOME",
  "HEAVY",
  "+1 INJURY MODIFIER",
  "+2 INJURY MODIFIER",
  "-1 INJURY MODIFIER",
  "CRITICAL",
  'BLAST X"',
  "SCATTER",
  "ASSAULT",
  "BAYONET LUG",
  "SHIELD COMBO",
];

export const ARMOUR_KEYWORDS = ["IMPERVIOUS"];

export const EQUIPMENT_KEYWORDS = [
  "NEGATE FEAR",
  "NEGATE GAS",
  "NEGATE FIRE",
  "NEGATE SHRAPNEL",
];

export const ITEM_CATEGORIES = [
  "melee",
  "ranged",
  "armour",
  "shield",
  "grenade",
  "equipment",
];

export const SKILL_TABLES = {
  Melee: [
    "Stand Firm",
    "Parry",
    "Close Quarters Combat",
    "Relentless Charge",
    "Melee Proficiency",
    "Strength of Samson",
    "Hard as Nails",
    "Surgical Strike",
    "Champion",
  ],
  Ranged: [
    "Hunter",
    "Gunslinger",
    "Far Shot",
    "Sharp Eyes",
    "Ranged Proficiency",
    "Sniper's Nest",
    "Point Blank",
    "Hip Shot",
    "Headshot",
  ],
  Stealth_and_Speed: [
    "Sixth Sense",
    "Assassinate",
    "Shadow Walker",
    "Athletic",
    "Sprinter",
    "Disengage",
    "Incoming",
    "Nimble",
    "Dodge",
  ],
  Wildcard: [
    "War Luck",
    "'Tis but a Scratch",
    "Bad Company",
    "Scavenger",
    "Skill & Expertise",
    "Show Off",
    "Friends in High Places",
    "Glory Hound",
    "War Stories",
  ],
};

export const EXPLORATION_LOCATIONS = {
  common: {
    4: {
      name: "Moonshine Stash",
      effect:
        "+2 Morale dice next game (New Antioch/Pilgrims) | Destroy: 2 Elites +1 XP | Sell: +30₡",
    },
    5: {
      name: "Heavy Weapons Cache",
      effect: "Surplus: +1 Heavy Battlekit | Specialise: Glory Item ≤5☼",
    },
    6: {
      name: "Trench Shrine",
      effect: "Troop Flag / Field Shrine / +2☼",
    },
    8: {
      name: "Ruined House",
      effect: "Equipment ≤30₡ or Glory Item ≤7☼",
    },
    9: {
      name: "Survivor",
      effect: "Add faction-specific basic trooper (no kit)",
    },
    10: {
      name: "Fallen Soldier",
      effect:
        "Ranged weapon ≤15₡ + Combat Helmet + Standard Armour + D6:4+ Medikit/Mountaineer Kit/Shovel",
    },
    11: {
      name: "Trench Merchant",
      effect: "+2☼ or unlock buying Glory ≤5☼",
    },
    14: {
      name: "Map & Document Bag",
      effect: 'Gain "Reroll Exploration" campaign skill',
    },
    16: {
      name: "Sniper's Lair",
      effect: "Faction-specific Sniper kit (or cash equivalent)",
    },
    18: {
      name: "Fallen Knight",
      effect:
        "Reinforced Armour + Trench Shield + Helmet + Sword/Polearm or +2☼",
    },
    20: {
      name: "Warband Strongbox",
      effect: "Battlekit ≤120₡ or Glory Items ≤9☼ (Weapons −1☼ cost)",
    },
  },
  rare: {
    5: {
      name: "Angelic Instrument",
      effect: 'Add 8" range instrument',
    },
    9: {
      name: "Prophetic Radio Post",
      effect: 'Gain "Extra Dice" campaign skill',
    },
    11: {
      name: "Pot of Manna",
      effect: "+10₡ every Exploration step (permanent)",
    },
    12: {
      name: "Alchemist Workshop",
      effect: "Curative Fluids (remove 1 Scar once)",
    },
    15: {
      name: "Black Market",
      effect: "Unlock buying Glory ≤8☼",
    },
    17: {
      name: "Book of Golems",
      effect: "Takwin Homunculus + free formulas ≤50₡",
    },
    19: {
      name: "Ruined Church",
      effect: "+4☼ or Glory ≤10☼",
    },
    21: {
      name: "Drugs & Erotica",
      effect: "+1 permanent Morale die OR +120₡ OR +4☼ (Pilgrims must take ☼)",
    },
    23: {
      name: "Saint's Reliquary",
      effect: "Relic or Screaming Skull",
    },
    25: {
      name: "High-Ranking Captive",
      effect: '+100₡ / +4☼ / Glory ≤8☼ / "Set Dice" skill',
    },
    32: {
      name: "Resurrection Machines",
      effect: "Salvaged Resurrection Machine (remove 1 Scar once)",
    },
  },
  legendary: {
    6: {
      name: "Battlefield of Corpses",
      effect: "Battlekit ≤100₡ (up to 2 items)",
    },
    8: {
      name: "Esoteric Library",
      effect: "Burn: 3+D3☼ | Plague | Sell 6D6×10₡ | Study: Blood Marker",
    },
    10: {
      name: "Hidden Passages",
      effect: '"Duplicate" campaign skill',
    },
    12: {
      name: "Jabirean Alchemical Book",
      effect: "Fire Shields or +150₡/5☼ etc.",
    },
    14: {
      name: "Black Network Contact",
      effect: "Unlock Glory ≤12☼",
    },
    16: {
      name: "Treasure of the Holies",
      effect: "D3 VP + 1 free Glory Item",
    },
    18: {
      name: "Saint's Skull",
      effect: "Holy or Unholy version",
    },
    20: {
      name: "Lock of Samson's Hair",
      effect: "STRONG + +1 Injury Die relic",
    },
    23: {
      name: "Patron's Visit",
      effect: "Trade up to 10☼ → equal VP",
    },
    26: {
      name: "Holy DNA Sample",
      effect: "Chosen model can turn Blood/Infection → Blessing",
    },
    30: {
      name: "Golgotha Tektites",
      effect: "2 Armours negate Fire/Gas/Shrapnel or NA +15☼",
    },
    36: {
      name: "Fruit of Knowledge",
      effect: "Chosen model gains DEMONIC + 1 free Skill",
    },
  },
};

export const getLocationByRoll = (table: string, roll: number) => {
  const tableData = EXPLORATION_LOCATIONS[table as keyof typeof EXPLORATION_LOCATIONS];
  if (!tableData) return null;
  return (tableData as any)[roll] || null;
};

export const XP_THRESHOLDS = [2, 4, 7, 10, 13, 16, 19, 22, 25];

// Helper functions
export const generateId = () => Math.random().toString(36).substr(2, 9);

// Apply upgrades to a model based on its template and enabled upgrades
export const applyModelUpgrades = (model: Model, template: ModelTemplate): Model => {
  let upgradedModel = { ...model };

  // Apply template upgrades (enabled ones)
  if (template.upgrades && model.enabledUpgrades && model.enabledUpgrades.length > 0) {
    model.enabledUpgrades.forEach(upgradeId => {
      const upgrade = template.upgrades?.find(u => u.id === upgradeId);
      if (!upgrade) return;

      applyUpgradeEffect(upgradedModel, upgrade);
    });
  }

  // Apply model-specific upgrades (all of them)
  if (model.upgrades && model.upgrades.length > 0) {
    model.upgrades.forEach(upgrade => {
      applyUpgradeEffect(upgradedModel, upgrade);
    });
  }

  return upgradedModel;
};

// Helper function to apply a single upgrade effect to a model
const applyUpgradeEffect = (model: Model, upgrade: any) => {
  // Handle legacy single effect upgrades (backward compatibility)
  if (upgrade.effect && upgrade.effect.type) {
    if (upgrade.effect.type === 'add_keywords' && upgrade.effect.keywords) {
      // Add keywords to the model
      const existingKeywords = new Set(model.keywords);
      upgrade.effect.keywords.forEach((keyword: string) => existingKeywords.add(keyword));
      model.keywords = Array.from(existingKeywords);
    } else if (upgrade.effect.type === 'modify_stats' && upgrade.effect.statChanges) {
        // Modify stats
        model.stats = { ...model.stats };
        Object.entries(upgrade.effect.statChanges).forEach(([statName, change]) => {
          const currentValue = (model.stats as any)[statName];
          if (typeof currentValue === 'number' && typeof change === 'number') {
            (model.stats as any)[statName] = currentValue + change;
          }
        });
    } else if (upgrade.effect.type === 'add_abilities' && upgrade.effect.abilities) {
      // Add abilities to the model
      const existingAbilities = Array.isArray(model.abilities) ? [...model.abilities] : [];

      // If existing abilities are strings, convert to objects for consistency
      const normalizedAbilities: Array<{ name: string; description: string }> = existingAbilities.map((ability: any) => {
        if (typeof ability === 'string') {
          return { name: ability, description: '' };
        }
        return ability;
      });

      upgrade.effect.abilities.forEach((ability: { name: string; description: string }) => {
        // Check if ability already exists (by name) to avoid duplicates
        const existingIndex = normalizedAbilities.findIndex((a) => a.name === ability.name);

        if (existingIndex === -1) {
          // Add new ability
          normalizedAbilities.push(ability);
        } else {
          // Update existing ability
          normalizedAbilities[existingIndex] = ability;
        }
      });

      model.abilities = normalizedAbilities;
    }
  }

  // Handle new multiple effects upgrades
  if (upgrade.effects) {
    // Add keywords
    if (upgrade.effects.addKeywords) {
      const existingKeywords = new Set(model.keywords);
      upgrade.effects.addKeywords.forEach((keyword: string) => existingKeywords.add(keyword));
      model.keywords = Array.from(existingKeywords);
    }

    // Modify stats
    if (upgrade.effects.modifyStats) {
      model.stats = { ...model.stats };
      Object.entries(upgrade.effects.modifyStats).forEach(([statName, change]) => {
        const currentValue = (model.stats as any)[statName];
        if (typeof currentValue === 'number' && typeof change === 'number') {
          (model.stats as any)[statName] = currentValue + change;
        }
      });
    }

    // Add abilities
    if (upgrade.effects.addAbilities) {
      const existingAbilities = Array.isArray(model.abilities) ? [...model.abilities] : [];

      // If existing abilities are strings, convert to objects for consistency
      const normalizedAbilities: Array<{ name: string; description: string }> = existingAbilities.map((ability: any) => {
        if (typeof ability === 'string') {
          return { name: ability, description: '' };
        }
        return ability;
      });

      upgrade.effects.addAbilities.forEach((ability: { name: string; description: string }) => {
        // Check if ability already exists (by name) to avoid duplicates
        const existingIndex = normalizedAbilities.findIndex((a) => a.name === ability.name);

        if (existingIndex === -1) {
          // Add new ability
          normalizedAbilities.push(ability);
        } else {
          // Update existing ability
          normalizedAbilities[existingIndex] = ability;
        }
      });

      model.abilities = normalizedAbilities;
    }

    // Add inbuilt equipment
    if (upgrade.effects.addInbuiltEquipment) {
      const existingItems = model.items || [];

      upgrade.effects.addInbuiltEquipment.forEach((equipment: any) => {
        // Check if equipment already exists (by name) to avoid duplicates
        const equipmentExists = existingItems.some((itemId) => {
          // This is a simplified check - in a real implementation you'd need to look up the item by ID
          // For now, we'll assume equipment IDs are unique and just add them
          return false; // TODO: Implement proper duplicate checking
        });

        if (!equipmentExists) {
          // Add equipment ID to items array
          existingItems.push(equipment.id || generateId());
        }
      });

      model.items = existingItems;
    }

    // Add items from templates
    if (upgrade.effects.addItems) {
      const existingItems = model.items || [];
      const newItems = [...existingItems];

      upgrade.effects.addItems.forEach((itemTemplateId: string) => {
        // Check if item already exists to avoid duplicates
        if (!newItems.includes(itemTemplateId)) {
          newItems.push(itemTemplateId);
        }
      });

      model.items = newItems;
    }
  }
};

// Calculate total upgrade cost for a model
export const calculateUpgradeCost = (template: ModelTemplate, enabledUpgrades: string[], modelUpgrades?: any[]): number => {
  let totalCost = 0;

  // Template upgrades cost
  if (template.upgrades) {
    totalCost += template.upgrades
      .filter(upgrade => enabledUpgrades.includes(upgrade.id))
      .reduce((total, upgrade) => total + (upgrade.cost || 0), 0);
  }

  // Model-specific upgrades cost (all of them)
  if (modelUpgrades) {
    totalCost += modelUpgrades.reduce((total, upgrade) => total + (upgrade.cost || 0), 0);
  }

  return totalCost;
};

// Calculate total value of a model (base cost + equipment + upgrades)
export const calculateModelValue = (
  model: Model,
  allItems: Item[],
  template?: ModelTemplate
): { totalDucats: number; totalGlory: number } => {
  let totalDucats = 0;
  let totalGlory = 0;

  // Base model cost
  const baseCost = model.cost || 0;
  const baseCurrency = model.costCurrency || "ducats";
  if (baseCurrency === "ducats") {
    totalDucats += baseCost;
  } else if (baseCurrency === "glory") {
    totalGlory += baseCost;
  }

  // Equipment costs
  const modelItems = allItems.filter(item => model.items.includes(item.id));
  modelItems.forEach(item => {
    const itemCost = item.cost || 0;
    const itemCurrency = item.costCurrency || "ducats";
    if (itemCurrency === "ducats") {
      totalDucats += itemCost;
    } else if (itemCurrency === "glory") {
      totalGlory += itemCost;
    }
  });

  // Upgrade costs (only ducats)
  if (template) {
    const enabledUpgrades = model.enabledUpgrades || [];
    const upgradeCost = calculateUpgradeCost(template, enabledUpgrades, model.upgrades);
    totalDucats += upgradeCost;
  }

  return { totalDucats, totalGlory };
};

export const calculateTotalWarbandValue = (
  models: Model[],
  items: Item[],
  templates: ModelTemplate[]
): { totalDucats: number; totalGlory: number } => {
  let totalDucats = 0;
  let totalGlory = 0;

  const aliveModels = models.filter(m => m.alive && !m.reserve);

  aliveModels.forEach(model => {
    const template = templates.find(t => t.id === model.id || t.name === model.name);
    const modelValue = calculateModelValue(model, items, template);
    totalDucats += modelValue.totalDucats;
    totalGlory += modelValue.totalGlory;
  });

  return { totalDucats, totalGlory };
};

export const loadData = () => {
  const warband = safeParse("tc_warband", {
    ducats: 700,
    glory: 0,
    battles: 0,
    explorationSkills: [],
    name: "My Warband",
  });
  const models = safeParse("tc_models", []);
  const items = safeParse("tc_items", []);
  const locations = safeParse("tc_locations", []);
  return { warband, models, items, locations };
};

export const saveWarband = (w: Warband): void =>
  localStorage.setItem("tc_warband", JSON.stringify(w));

export const saveModels = (m: Model[]): void =>
  localStorage.setItem("tc_models", JSON.stringify(m));

export const saveItems = (i: Item[]): void =>
  localStorage.setItem("tc_items", JSON.stringify(i));

export const saveLocations = (l: ExplorationLocation[]): void =>
  localStorage.setItem("tc_locations", JSON.stringify(l));

export const calculateThresholds = (battles: number) => ({
  ducatThreshold: 700 + battles * 100,
  maxModels: 10 + Math.floor(battles / 1),
});

export const calculateFinalArmour = (model: Model, allItems: Item[]) => {
  const modelItems = allItems.filter((item) => model.items.includes(item.id));
  const armourModifier = modelItems
    .filter((item) => item.category === "armour" || item.category === "shield")
    .reduce((sum, item) => sum + (parseInt(item.armourModifier || "0") || 0), 0);
  return {
    finalArmour: model.stats.baseArmour + armourModifier,
    modifier: armourModifier,
  };
};

// Parse battle scar effects into stat modifiers
export const parseBattleScarEffects = (scars: string[]): { [statName: string]: number } => {
  const statChanges: { [statName: string]: number } = {};

  scars.forEach(scar => {
    const lowerScar = scar.toLowerCase();
    // Legacy battle scars (for backward compatibility)
    if (lowerScar.includes("weakened arm")) {
      statChanges.melee = (statChanges.melee || 0) - 1;
    }
    if (lowerScar.includes("lame leg")) {
      statChanges.movement = (statChanges.movement || 0) - 1;
    }
    if (lowerScar.includes("blind eye")) {
      statChanges.ranged = (statChanges.ranged || 0) - 1;
    }
    if (lowerScar.includes("chest wound") && lowerScar.includes("-1")) {
      statChanges.finalArmour = (statChanges.finalArmour || 0) - 1;
    }

    // New injury system effects that affect stats
    if (scar === "Lost an Eye") {
      statChanges.ranged = (statChanges.ranged || 0) - 1;
    }
    if (scar === "Leg Wound") {
      statChanges.movement = (statChanges.movement || 0) - 2;
    }
  });

  return statChanges;
};

// Calculate effective combat stats including all modifiers
export const calculateEffectiveStats = (model: Model, allItems: Item[], template?: ModelTemplate): ModelStats => {
  // Start with base stats.
  // If we have a template, prefer its base stats to avoid persisting/stacking
  // previously-applied upgrade modifiers into the model itself.
  let effectiveStats = { ...(template?.stats || model.stats) };

  // Apply upgrade effects (both template and model-specific)
  if (template) {
    // Apply template upgrades
    if (template.upgrades && model.enabledUpgrades && model.enabledUpgrades.length > 0) {
      model.enabledUpgrades.forEach(upgradeId => {
        const upgrade = template.upgrades?.find(u => u.id === upgradeId);
        if (upgrade && upgrade.effects.modifyStats) {
          Object.entries(upgrade.effects.modifyStats).forEach(([statName, change]) => {
            const currentValue = (effectiveStats as any)[statName];
            if (typeof currentValue === 'number' && typeof change === 'number') {
              (effectiveStats as any)[statName] = currentValue + change;
            }
          });
        }
      });
    }
  }

  // Apply model-specific upgrades
  if (model.upgrades && model.upgrades.length > 0) {
    model.upgrades.forEach(upgrade => {
      if (upgrade.effects.modifyStats) {
        Object.entries(upgrade.effects.modifyStats).forEach(([statName, change]) => {
          const currentValue = (effectiveStats as any)[statName];
          if (typeof currentValue === 'number' && typeof change === 'number') {
            (effectiveStats as any)[statName] = currentValue + change;
          }
        });
      }
    });
  }

  // Apply item effects (armour modifiers)
  const modelItems = allItems.filter((item) => model.items.includes(item.id));
  const armourModifier = modelItems
    .filter((item) => item.armourModifier) // Any item with an armor modifier contributes
    .reduce((sum, item) => sum + (parseInt(item.armourModifier || "0") || 0), 0);

  effectiveStats.finalArmour = effectiveStats.baseArmour + armourModifier;

  // Apply battle scar effects
  const scarStatChanges = parseBattleScarEffects(model.scars);
  Object.entries(scarStatChanges).forEach(([statName, change]) => {
    const currentValue = (effectiveStats as any)[statName];
    if (typeof currentValue === 'number' && typeof change === 'number') {
      (effectiveStats as any)[statName] = currentValue + change;
    }
  });

  return effectiveStats;
};

export const shouldTriggerSkillModal = (newXP: number, oldXP: number, skillCount: number): boolean => {
  for (let i = 0; i < XP_THRESHOLDS.length; i++) {
    if (
      newXP >= XP_THRESHOLDS[i] &&
      oldXP < XP_THRESHOLDS[i] &&
      skillCount <= i
    )
      return true;
  }
  return false;
};

export const formatCost = (cost: number, currency: string): string => {
  if (currency === "free") return "Free";
  if (currency === "glory") return `${cost} ☼`;
  return `${cost} 👑`;
};

export const getModelBackgroundUrl = (model: Model): string | undefined => {
  if (model.customBgUrl) return model.customBgUrl;

  const key = normalizeBackgroundKey(model.class);
  if (!key) return undefined;

  return `/images/models/backgrounds/${key}.webp`;
};

export const exportWarbandToText = (warband: Warband, models: Model[], allItems: Item[], templates: ModelTemplate[], itemTemplates?: ItemTemplate[]): string => {
  const totalWarbandValue = calculateTotalWarbandValue(models, allItems, templates);
  let output = `## ${warband.name} ##\n\n`;
  output += `Rating: ${totalWarbandValue.totalDucats} Ducats | ${totalWarbandValue.totalGlory} Glory\n\n`;

  // Group models by type (excluding reserved models)
  const aliveModels = models.filter(m => m.alive);
  const activeModels = aliveModels.filter(m => !m.reserve);
  const reservedModels = aliveModels.filter(m => m.reserve);
  
  const elites = activeModels.filter(m => m.type === "elite" && m.costCurrency !== "glory");
  const troops = activeModels.filter(m => m.type === "trooper" && m.costCurrency !== "glory");
  const mercenaries = activeModels.filter(m => m.costCurrency === "glory");

  // Elites section
  if (elites.length > 0) {
    output += "## Elites ##\n\n";
    elites.forEach(model => {
      const template = templates.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);
      const modelValue = calculateModelValue(model, allItems, template);
      const modelItems = allItems.filter(item => model.items.includes(item.id));
      const equipmentList = modelItems.map(item => item.name).join(", ");

      // Get items and abilities added by upgrades
      const upgradeAddedItems: string[] = [];
      const upgradeAddedAbilities: string[] = [];

      if (template && model.enabledUpgrades && itemTemplates) {
        model.enabledUpgrades.forEach(upgradeId => {
          const upgrade = template.upgrades?.find(u => u.id === upgradeId);
          if (upgrade?.effects?.addItems) {
            upgrade.effects.addItems.forEach(itemTemplateId => {
              const itemTemplate = itemTemplates.find(it => it.templateName === itemTemplateId || it.name === itemTemplateId);
              if (itemTemplate) {
                upgradeAddedItems.push(itemTemplate.name);
              }
            });
          }
          if (upgrade?.effects?.addAbilities) {
            upgrade.effects.addAbilities.forEach(ability => {
              upgradeAddedAbilities.push(ability.name);
            });
          }
        });
      }

      output += `${model.name}\n`;
      output += `• Cost: ${modelValue.totalDucats} Ducats | ${modelValue.totalGlory} Glory\n`;
      output += `• XP: ${model.xp}\n`;
      if (equipmentList) {
        output += `• Equipment: ${equipmentList}\n`;
      }
      if (upgradeAddedItems.length > 0) {
        output += `• Upgrade Items: ${upgradeAddedItems.join(", ")}\n`;
      }
      if (upgradeAddedAbilities.length > 0) {
        output += `• Upgrade Abilities: ${upgradeAddedAbilities.join(", ")}\n`;
      }

      // Add upgrades
      if (model.enabledUpgrades && model.enabledUpgrades.length > 0 && template) {
        const enabledUpgradeNames = model.enabledUpgrades
          .map(upgradeId => {
            const upgrade = template.upgrades?.find(u => u.id === upgradeId);
            return upgrade ? upgrade.name : upgradeId;
          })
          .filter(name => name);
        if (enabledUpgradeNames.length > 0) {
          output += `• Upgrades: ${enabledUpgradeNames.join(", ")}\n`;
        }
      }

      if (model.skills && model.skills.length > 0) {
        output += `• Skills: ${model.skills.map(s => typeof s === 'string' ? s : s.name).join(", ")}\n`;
      }
      if (model.scars && model.scars.length > 0) {
        output += `• Injuries: ${model.scars.join(", ")}\n`;
      }
      output += "\n";
    });
  }

  // Troops section
  if (troops.length > 0) {
    output += "## Troops ##\n\n";
    troops.forEach(model => {
      const template = templates.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);
      const modelValue = calculateModelValue(model, allItems, template);
      const modelItems = allItems.filter(item => model.items.includes(item.id));
      const equipmentList = modelItems.map(item => item.name).join(", ");

      // Get items and abilities added by upgrades
      const upgradeAddedItems: string[] = [];
      const upgradeAddedAbilities: string[] = [];

      if (template && model.enabledUpgrades && itemTemplates) {
        model.enabledUpgrades.forEach(upgradeId => {
          const upgrade = template.upgrades?.find(u => u.id === upgradeId);
          if (upgrade?.effects?.addItems) {
            upgrade.effects.addItems.forEach(itemTemplateId => {
              const itemTemplate = itemTemplates.find(it => it.templateName === itemTemplateId || it.name === itemTemplateId);
              if (itemTemplate) {
                upgradeAddedItems.push(itemTemplate.name);
              }
            });
          }
          if (upgrade?.effects?.addAbilities) {
            upgrade.effects.addAbilities.forEach(ability => {
              upgradeAddedAbilities.push(ability.name);
            });
          }
        });
      }

      output += `${model.name}\n`;
      output += `• Cost: ${modelValue.totalDucats} Ducats | ${modelValue.totalGlory} Glory\n`;
      output += `• XP: ${model.xp}\n`;
      if (equipmentList) {
        output += `• Equipment: ${equipmentList}\n`;
      }
      if (upgradeAddedItems.length > 0) {
        output += `• Upgrade Items: ${upgradeAddedItems.join(", ")}\n`;
      }
      if (upgradeAddedAbilities.length > 0) {
        output += `• Upgrade Abilities: ${upgradeAddedAbilities.join(", ")}\n`;
      }

      // Add upgrades
      if (model.enabledUpgrades && model.enabledUpgrades.length > 0 && template) {
        const enabledUpgradeNames = model.enabledUpgrades
          .map(upgradeId => {
            const upgrade = template.upgrades?.find(u => u.id === upgradeId);
            return upgrade ? upgrade.name : upgradeId;
          })
          .filter(name => name);
        if (enabledUpgradeNames.length > 0) {
          output += `• Upgrades: ${enabledUpgradeNames.join(", ")}\n`;
        }
      }

      output += "\n";
    });
  }

  // Mercenaries section
  if (mercenaries.length > 0) {
    output += "## Mercenaries ##\n\n";
    mercenaries.forEach(model => {
      const template = templates.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);
      const modelValue = calculateModelValue(model, allItems, template);
      const modelItems = allItems.filter(item => model.items.includes(item.id));
      const equipmentList = modelItems.map(item => item.name).join(", ");

      // Get items and abilities added by upgrades
      const upgradeAddedItems: string[] = [];
      const upgradeAddedAbilities: string[] = [];

      if (template && model.enabledUpgrades && itemTemplates) {
        model.enabledUpgrades.forEach(upgradeId => {
          const upgrade = template.upgrades?.find(u => u.id === upgradeId);
          if (upgrade?.effects?.addItems) {
            upgrade.effects.addItems.forEach(itemTemplateId => {
              const itemTemplate = itemTemplates.find(it => it.templateName === itemTemplateId || it.name === itemTemplateId);
              if (itemTemplate) {
                upgradeAddedItems.push(itemTemplate.name);
              }
            });
          }
          if (upgrade?.effects?.addAbilities) {
            upgrade.effects.addAbilities.forEach(ability => {
              upgradeAddedAbilities.push(ability.name);
            });
          }
        });
      }

      output += `${model.name}\n`;
      output += `• Cost: ${modelValue.totalDucats} Ducats | ${modelValue.totalGlory} Glory\n`;
      output += `• XP: ${model.xp}\n`;
      if (equipmentList) {
        output += `• Equipment: ${equipmentList}\n`;
      }
      if (upgradeAddedItems.length > 0) {
        output += `• Upgrade Items: ${upgradeAddedItems.join(", ")}\n`;
      }
      if (upgradeAddedAbilities.length > 0) {
        output += `• Upgrade Abilities: ${upgradeAddedAbilities.join(", ")}\n`;
      }

      // Add upgrades
      if (model.enabledUpgrades && model.enabledUpgrades.length > 0 && template) {
        const enabledUpgradeNames = model.enabledUpgrades
          .map(upgradeId => {
            const upgrade = template.upgrades?.find(u => u.id === upgradeId);
            return upgrade ? upgrade.name : upgradeId;
          })
          .filter(name => name);
        if (enabledUpgradeNames.length > 0) {
          output += `• Upgrades: ${enabledUpgradeNames.join(", ")}\n`;
        }
      }

      output += "\n";
    });
  }

  // Reserved Models section
  if (reservedModels.length > 0) {
    output += "## Reserved Models ##\n\n";
    reservedModels.forEach(model => {
      const template = templates.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);
      const modelValue = calculateModelValue(model, allItems, template);
      const modelItems = allItems.filter(item => model.items.includes(item.id));
      const equipmentList = modelItems.map(item => item.name).join(", ");

      // Get items and abilities added by upgrades
      const upgradeAddedItems: string[] = [];
      const upgradeAddedAbilities: string[] = [];

      if (template && model.enabledUpgrades && itemTemplates) {
        model.enabledUpgrades.forEach(upgradeId => {
          const upgrade = template.upgrades?.find(u => u.id === upgradeId);
          if (upgrade?.effects?.addItems) {
            upgrade.effects.addItems.forEach(itemTemplateId => {
              const itemTemplate = itemTemplates.find(it => it.templateName === itemTemplateId || it.name === itemTemplateId);
              if (itemTemplate) {
                upgradeAddedItems.push(itemTemplate.name);
              }
            });
          }
          if (upgrade?.effects?.addAbilities) {
            upgrade.effects.addAbilities.forEach(ability => {
              upgradeAddedAbilities.push(ability.name);
            });
          }
        });
      }

      output += `${model.name}\n`;
      output += `• Cost: ${modelValue.totalDucats} Ducats | ${modelValue.totalGlory} Glory\n`;
      output += `• XP: ${model.xp}\n`;
      if (equipmentList) {
        output += `• Equipment: ${equipmentList}\n`;
      }
      if (upgradeAddedItems.length > 0) {
        output += `• Upgrade Items: ${upgradeAddedItems.join(", ")}\n`;
      }
      if (upgradeAddedAbilities.length > 0) {
        output += `• Upgrade Abilities: ${upgradeAddedAbilities.join(", ")}\n`;
      }

      // Add upgrades
      if (model.enabledUpgrades && model.enabledUpgrades.length > 0 && template) {
        const enabledUpgradeNames = model.enabledUpgrades
          .map(upgradeId => {
            const upgrade = template.upgrades?.find(u => u.id === upgradeId);
            return upgrade ? upgrade.name : upgradeId;
          })
          .filter(name => name);
        if (enabledUpgradeNames.length > 0) {
          output += `• Upgrades: ${enabledUpgradeNames.join(", ")}\n`;
        }
      }

      if (model.skills && model.skills.length > 0) {
        output += `• Skills: ${model.skills.map(s => typeof s === 'string' ? s : s.name).join(", ")}\n`;
      }
      if (model.scars && model.scars.length > 0) {
        output += `• Injuries: ${model.scars.join(", ")}\n`;
      }
      output += "\n";
    });
  }

  // Stash section
  output += "## Stash ##\n\n";
  output += `Stashed Ducats: ${warband.ducats}\n`;
  output += `Unspent Glory: ${warband.glory}\n`;

  return output;
};

export const exportToTTS = (model: Model, allItems: Item[], modelTemplates?: ModelTemplate[], itemTemplates?: ItemTemplate[]): string => {
  const modelItems = allItems.filter((item) => model.items.includes(item.id));
  const equipmentKeywords = modelItems
    .filter((item) => item.category === "equipment")
    .flatMap((item) => item.keywords);
  const allModelKeywords = Array.from(new Set([...model.keywords, ...equipmentKeywords]));

  // Find model template and calculate effective stats
  const modelTemplate = modelTemplates?.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);
  const effectiveStats = calculateEffectiveStats(model, allItems, modelTemplate);


  let output = `[b]☩☩ ${model.name.toUpperCase()} ☩☩[/b]\n(${model.class})\n`;
  output += `[b][89E1E3]${effectiveStats.movement}"/Infantry[-][/b] | [b][fa8231]Mel. +${effectiveStats.melee}D[-][/b] | `;
  output += `[b][C966E5]Ran. +${effectiveStats.ranged}D[-][/b] | [b][b2b7be]Arm. ${effectiveStats.finalArmour}[-][/b]\n`;
  if (allModelKeywords.length > 0) output += allModelKeywords.join(", ") + "\n";
  output += "\n";

  modelItems
    .filter((i) => i.category === "melee")
    .forEach((w) => {
      output += `[b][fa8231]✞ ${w.name}[-][/b]\nMelee`;
      if (w.keywords.length > 0) output += ` | ${w.keywords.join(", ")}`;
      output += "\n";
      if (w.notes) output += `${w.notes}\n`;
    });

  modelItems
    .filter((i) => i.category === "ranged")
    .forEach((w) => {
      output += `[b][C966E5]⊕ ${w.name}[-][/b]\n`;
      const parts = [];
      if (w.range) parts.push(w.range + '"');
      if (w.keywords.length > 0) parts.push(w.keywords.join(", "));
      output += parts.join(" | ") + "\n";
      if (w.notes) output += `${w.notes}\n`;
    });

  modelItems
    .filter((i) => i.category === "armour")
    .forEach((a) => {
      output += `[b][b2b7be]❖ ${a.name}[-][/b]\n`;
      if (a.armourModifier) output += `${a.armourModifier} INJURY MODIFIER\n`;
      if (a.notes) output += `${a.notes}\n`;
    });

  modelItems
    .filter((i) => i.category === "shield")
    .forEach((s) => {
      output += `[b][fa8231]☩ ${s.name}[-][/b]\n`;
      if (s.armourModifier) output += `${s.armourModifier} INJURY MODIFIER\n`;
      if (s.notes) output += `${s.notes}\n`;
    });

  modelItems
    .filter((i) => i.category === "grenade")
    .forEach((g) => {
      output += `[b][fa8231]✢ ${g.name}[-][/b]\n`;
      const parts = [];
      if (g.range) parts.push(g.range + '"');
      if (g.keywords.length > 0) parts.push(g.keywords.join(", "));
      output += parts.join(" | ") + "\n";
      if (g.notes) output += `${g.notes}\n`;
    });

  modelItems
    .filter((i) => i.category === "equipment")
    .forEach((e) => {
      output += `[b][f7b731]✜ ${e.name}[-][/b]\n`;
      if (e.keywords.length > 0) output += e.keywords.join(", ") + "\n";
      if (e.notes) output += `${e.notes}\n`;
    });

  // Add upgrades
  if (model.enabledUpgrades && model.enabledUpgrades.length > 0 && modelTemplate) {
    output += "\n";
    model.enabledUpgrades.forEach(upgradeId => {
      const upgrade = modelTemplate.upgrades?.find(u => u.id === upgradeId);
      if (upgrade) {
        output += `[b][f39c12]⬆ ${upgrade.name}[-][/b]\n\n`;
      }
    });
  }

  if (model.abilities.length > 0) {
    output += "\n";
    model.abilities.forEach((a) => {
      const name = typeof a === "string" ? a : a.name || "Unnamed Ability";
      const desc = typeof a === "string" ? "" : a.description || "";
      output += `[b][0fb9b1]✧ ${name}[-][/b]\n${desc}\n`;
    });
  }

  if (model.skills.length > 0) {
    output += "\n";
    model.skills.forEach((s) => (output += `[b][20bf6b]△ ${s.name}[-][/b]\n`));
  }

  if (model.scars.length > 0) {
    output += "\n";
    model.scars.forEach((s) => (output += `[b][eb3b5a]▽ ${s}[-][/b]\n`));
  }

  return output;
};

export const saveModelTemplates = (templates: ModelTemplate[]): void =>
  localStorage.setItem("tc_model_templates", JSON.stringify(templates));

export const saveItemTemplates = (templates: ItemTemplate[]): void =>
  localStorage.setItem("tc_item_templates", JSON.stringify(templates));

// Limit validation functions

// Count current instances of a model class in warband
export const countModelClassInstances = (models: Model[], className: string): number => {
  return models.filter(model => model.class === className && model.alive).length;
};

// Count current instances of items by category/name across warband + all models
export const countItemInstances = (
  warband: Warband,
  items: Item[],
  models: Model[],
  criteria: 'name' | 'templateName',
  value: string,
  itemTemplates: ItemTemplate[]
): number => {
  let targetValue = value;

  // If criteria is 'templateName', find the template and use its name
  if (criteria === 'templateName') {
    const template = itemTemplates.find(t => t.templateName === value);
    if (template) {
      targetValue = template.name;
    }
  }

  // Count in warchest
  const warchestCount = warband.warchest
    .map(itemId => items.find(item => item.id === itemId))
    .filter(item => item && item.name === targetValue)
    .length;

  // Count equipped on models (only alive models)
  const equippedCount = models
    .filter(model => model.alive)
    .flatMap(model => model.items)
    .map(itemId => items.find(item => item.id === itemId))
    .filter(item => item && item.name === targetValue)
    .length;

  return warchestCount + equippedCount;
};

// Check if adding a model would violate limits
export const canAddModel = (
  template: ModelTemplate,
  models: Model[]
): { allowed: boolean; reason?: string } => {
  if (!template.limit) return { allowed: true };

  // Always use class scope for model limits
  const currentCount = countModelClassInstances(models, template.class);

  if (currentCount >= template.limit) {
    return {
      allowed: false,
      reason: `Cannot add more ${template.class} models (limit: ${template.limit})`
    };
  }

  return { allowed: true };
};

// Check if adding an item would violate limits
export const canAddItem = (
  template: ItemTemplate,
  warband: Warband,
  items: Item[],
  models: Model[],
  itemTemplates: ItemTemplate[]
): { allowed: boolean; reason?: string } => {
  if (!template.limit) return { allowed: true };

  // Always use name scope for item limits
  const value = template.name;
  const currentCount = countItemInstances(warband, items, models, 'name', value, itemTemplates);

  if (currentCount >= template.limit) {
    return {
      allowed: false,
      reason: `Cannot add more ${value} items (limit: ${template.limit})`
    };
  }

  return { allowed: true };
};

// Check if item can be equipped by model (restrictions)
export const canEquipItem = (
  itemTemplate: ItemTemplate,
  model: Model
): { allowed: boolean; reason?: string } => {
  if (!itemTemplate.restrictions || itemTemplate.restrictions.length === 0) {
    return { allowed: true };
  }

  for (const restriction of itemTemplate.restrictions) {
    if (restriction === "ELITE only" && model.type !== "elite") {
      return {
        allowed: false,
        reason: "This item can only be equipped by Elite models"
      };
    }
    // Add more restriction checks as needed
    // e.g., specific class restrictions, etc.
  }

  return { allowed: true };
};

// Get current count and limit info for display
export const getLimitDisplayInfo = (
  template: ModelTemplate | ItemTemplate,
  warband: Warband,
  items: Item[],
  models: Model[],
  itemTemplates?: ItemTemplate[]
): { current: number; limit: number | null; isAtLimit: boolean } => {
  if (!template.limit) {
    return { current: 0, limit: null, isAtLimit: false };
  }

  let current: number;

  if ('class' in template) {
    // Model template
    current = countModelClassInstances(models, template.class);
  } else {
    // Item template - always use name scope
    const value = template.name;
    current = countItemInstances(warband, items, models, 'name', value, itemTemplates || []);
  }

  return {
    current,
    limit: template.limit,
    isAtLimit: current >= template.limit
  };
};

export const loadModelTemplates = (): ModelTemplate[] => safeParse("tc_model_templates", []);

export const loadItemTemplates = (): ItemTemplate[] => safeParse("tc_item_templates", []);
